import { importLibrary, setOptions } from '@googlemaps/js-api-loader'

let placesLoadPromise: Promise<typeof google> | null = null
let mapsLoadPromise: Promise<typeof google> | null = null
let optionsApplied = false

const MAPS_LOAD_TIMEOUT_MS = 20000

function applyGoogleMapsOptions(): void {
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '').trim()
  if (!apiKey) {
    throw new Error('Missing VITE_GOOGLE_MAPS_API_KEY')
  }

  if (!optionsApplied) {
    setOptions({
      key: apiKey,
      v: 'weekly',
    })
    optionsApplied = true
  }
}

function withLoadTimeout<T>(promise: Promise<T>, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(message))
    }, MAPS_LOAD_TIMEOUT_MS)

    promise
      .then((value) => {
        window.clearTimeout(timer)
        resolve(value)
      })
      .catch((err: unknown) => {
        window.clearTimeout(timer)
        reject(err)
      })
  })
}

function formatMapsLoadError(err: unknown): Error {
  if (err instanceof Error && err.message.includes('超时')) {
    return err
  }

  const detail = err instanceof Error ? err.message : String(err)
  return new Error(
    `Google Maps 无法加载。请检查：1) 网络能否访问 Google；2) API Key 是否有效；3) Cloud Console 是否启用 Maps JavaScript API。${detail ? ` (${detail})` : ''}`,
  )
}

/** 全应用只加载一次 Maps JS（含 places），勿在组件 destroy 时卸 script */
export function loadGoogleMapsPlaces(): Promise<typeof google> {
  if (typeof window !== 'undefined' && window.google?.maps?.places) {
    return Promise.resolve(window.google)
  }
  if (placesLoadPromise) return placesLoadPromise

  applyGoogleMapsOptions()

  placesLoadPromise = withLoadTimeout(
    importLibrary('places').then(() => {
      if (!window.google?.maps?.places) {
        throw new Error('Google Maps places library failed to load')
      }
      return window.google
    }),
    'Google Maps Places 加载超时，请检查网络或 API Key 配置',
  ).catch((err: unknown) => {
    placesLoadPromise = null
    throw formatMapsLoadError(err)
  })

  return placesLoadPromise
}

/** 加载 Maps 渲染库（Tasks and Map 等页面） */
export function loadGoogleMaps(): Promise<typeof google> {
  if (typeof window !== 'undefined' && window.google?.maps?.Map) {
    return Promise.resolve(window.google)
  }
  if (mapsLoadPromise) return mapsLoadPromise

  applyGoogleMapsOptions()

  mapsLoadPromise = withLoadTimeout(
    importLibrary('maps').then(() => {
      if (!window.google?.maps?.Map) {
        throw new Error('Google Maps library failed to load')
      }
      return window.google
    }),
    'Google Maps 加载超时，请检查网络能否访问 Google，或 API Key 是否正确',
  ).catch((err: unknown) => {
    mapsLoadPromise = null
    throw formatMapsLoadError(err)
  })

  return mapsLoadPromise
}
